import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Calendar, User, Plus, Minus, Edit, MoveVertical, Code, Eye } from 'lucide-react';
import { BlueprintVersion } from '@/lib/stores/storeTypes';
import { formatVersion, getTimeAgo } from '@/lib/utils/versionUtils';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

interface VersionChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  version: BlueprintVersion;
}

export function VersionChangesModal({
  isOpen,
  onClose,
  version,
}: VersionChangesModalProps) {
  const { t } = usePlatformTranslation();
  const { getUserNameById } = useSimplifiedUserData();

  const versionString = formatVersion({
    major: version.major_version,
    minor: version.minor_version,
    patch: version.patch_version,
  });

  const userName = getUserNameById(version.created_by);

  // Check if this is the current active version (synthetic ID)
  const isCurrentActive = version.id?.startsWith('current-');

  // State for toggling between visual and JSON view
  const [showRawData, setShowRawData] = useState(false);

  // Parse delta changes into human-readable format
  interface ParsedChange {
    type: 'added' | 'removed' | 'modified' | 'reordered';
    category: string;
    path: string;
    description: string;
    oldValue?: any;
    newValue?: any;
  }

  const parseDeltaChanges = (delta: any): ParsedChange[] => {
    const changes: ParsedChange[] = [];

    if (!delta || typeof delta !== 'object') {
      return changes;
    }

    // Check if this is a jsondiffpatch delta or a full snapshot
    const isDeltaFormat = Object.values(delta).some(value => 
      Array.isArray(value) && value.length > 0
    );

    if (!isDeltaFormat) {
      // It's a full snapshot, not a delta
      return [];
    }

    // Helper to extract meaningful name from object
    const getObjectName = (obj: any): string => {
      if (!obj || typeof obj !== 'object') return '';
      return obj.title || obj.label || obj.name || '';
    };

    // Parse blueprint-specific changes
    const parseChange = (key: string, value: any, parentPath: string = '', parentContext?: any) => {
      const fullPath = parentPath ? `${parentPath}.${key}` : key;

      // Array format from jsondiffpatch:
      // [oldValue, newValue] - modified
      // [newValue] - added
      // [oldValue, 0, 0] - deleted
      // {_t: 'a', ...} - array changes
      
      if (Array.isArray(value)) {
        if (value.length === 3 && value[1] === 0 && value[2] === 0) {
          // Deleted
          const oldObj = value[0];
          const objectName = getObjectName(oldObj);
          
          changes.push({
            type: 'removed',
            category: getCategoryFromPath(fullPath),
            path: fullPath,
            description: getDescriptionFromPath(fullPath, 'removed', objectName),
            oldValue: objectName || (typeof oldObj === 'object' ? 'Object removed' : oldObj)
          });
        } else if (value.length === 1) {
          // Added
          const newObj = value[0];
          const objectName = getObjectName(newObj);
          
          changes.push({
            type: 'added',
            category: getCategoryFromPath(fullPath),
            path: fullPath,
            description: getDescriptionFromPath(fullPath, 'added', objectName),
            newValue: objectName || (typeof newObj === 'object' ? 'Object added' : newObj)
          });
        } else if (value.length === 2) {
          // Modified - this is a primitive value change
          const oldVal = value[0];
          const newVal = value[1];
          
          // Skip if both are objects (these will be handled by recursion)
          if (typeof oldVal !== 'object' || typeof newVal !== 'object') {
            changes.push({
              type: 'modified',
              category: getCategoryFromPath(fullPath),
              path: fullPath,
              description: getDescriptionFromPath(fullPath, 'modified'),
              oldValue: oldVal,
              newValue: newVal
            });
          }
        }
      } else if (value && typeof value === 'object' && value._t === 'a') {
        // Array changes (sections, fields, etc.)
        Object.keys(value).forEach(k => {
          if (k !== '_t') {
            parseChange(k, value[k], fullPath, value);
          }
        });
      } else if (value && typeof value === 'object') {
        // Nested object, recurse to find the actual changed properties
        Object.keys(value).forEach(k => {
          parseChange(k, value[k], fullPath, value);
        });
      }
    };

    Object.keys(delta).forEach(key => {
      parseChange(key, delta[key]);
    });

    return changes;
  };

  const getCategoryFromPath = (path: string): string => {
    if (path.includes('sections')) return 'Sections';
    if (path.includes('fields')) return 'Fields';
    if (path.includes('developer_notes')) return 'Developer Notes';
    if (path.includes('name')) return 'Blueprint Properties';
    if (path.includes('description')) return 'Blueprint Properties';
    if (path.includes('type')) return 'Blueprint Properties';
    if (path.includes('color')) return 'Blueprint Properties';
    if (path.includes('tags')) return 'Tags';
    return 'Other';
  };

  const getDescriptionFromPath = (path: string, changeType: string, objectName?: string): string => {
    const parts = path.split('.');
    
    // Handle sections
    if (path.includes('sections')) {
      const sectionMatch = path.match(/sections\.(\d+|_\d+)/);
      if (sectionMatch) {
        const sectionIndex = sectionMatch[1];
        
        if (path.includes('title')) {
          return objectName 
            ? `Section title ${changeType}`
            : `Section title ${changeType}`;
        }
        if (path.includes('description')) {
          return objectName 
            ? `Section "${objectName}" description ${changeType}`
            : `Section description ${changeType}`;
        }
        if (path.includes('order')) {
          return objectName 
            ? `Section "${objectName}" order ${changeType}`
            : `Section order ${changeType}`;
        }
        if (path.includes('fields')) {
          const fieldMatch = path.match(/fields\.(\d+|_\d+)/);
          if (fieldMatch) {
            if (path.includes('label')) {
              return objectName 
                ? `Field label ${changeType}`
                : `Field label ${changeType}`;
            }
            if (path.includes('type')) {
              return objectName 
                ? `Field "${objectName}" type ${changeType}`
                : `Field type ${changeType}`;
            }
            if (path.includes('required')) {
              return objectName 
                ? `Field "${objectName}" required status ${changeType}`
                : `Field required status ${changeType}`;
            }
            if (path.includes('placeholder')) {
              return objectName 
                ? `Field "${objectName}" placeholder ${changeType}`
                : `Field placeholder ${changeType}`;
            }
            if (path.includes('default_value')) {
              return objectName 
                ? `Field "${objectName}" default value ${changeType}`
                : `Field default value ${changeType}`;
            }
            if (path.includes('options')) {
              return objectName 
                ? `Field "${objectName}" options ${changeType}`
                : `Field options ${changeType}`;
            }
            // Whole field added/removed
            return objectName 
              ? `Field "${objectName}" ${changeType}`
              : `Field ${changeType}`;
          }
        }
        // Whole section added/removed
        return objectName 
          ? `Section "${objectName}" ${changeType}`
          : `Section ${changeType}`;
      }
    }

    // Handle blueprint properties
    if (path === 'name') return `Blueprint name ${changeType}`;
    if (path === 'description') return `Blueprint description ${changeType}`;
    if (path === 'type') return `Blueprint type ${changeType}`;
    if (path === 'color') return `Blueprint color ${changeType}`;
    
    // Handle developer notes
    if (path.includes('developer_notes')) {
      if (path.includes('text') || path.includes('content')) {
        return `Developer note content ${changeType}`;
      }
      return objectName 
        ? `Developer note "${objectName}" ${changeType}`
        : `Developer note ${changeType}`;
    }

    // Handle tags
    if (path.includes('tags')) {
      return objectName 
        ? `Tag "${objectName}" ${changeType}`
        : `Tag ${changeType}`;
    }

    // Clean up the path for display
    const cleanPath = path.split('.').pop() || path;
    return `${cleanPath.replace(/_/g, ' ')} ${changeType}`;
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'empty';
    if (typeof value === 'string') {
      // If it looks like a UUID, don't show it
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        return '(ID)';
      }
      return value.length > 100 ? `"${value.substring(0, 100)}..."` : `"${value}"`;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.length} items]`;
      }
      // For objects, try to extract meaningful info
      const name = value.title || value.label || value.name;
      if (name) return `"${name}"`;
      
      const keys = Object.keys(value);
      return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '...' : ''}}`;
    }
    return String(value);
  };

  const parsedChanges = parseDeltaChanges(version.delta);
  const groupedChanges = parsedChanges.reduce((acc, change) => {
    if (!acc[change.category]) {
      acc[change.category] = [];
    }
    acc[change.category].push(change);
    return acc;
  }, {} as Record<string, ParsedChange[]>);

  // Helper function to render raw JSON data
  const renderRawData = () => {
    console.log('version.delta', version.delta);
    if (!version.delta || typeof version.delta !== 'object') {
      return (
        <div className="text-sm text-gray-500 italic text-center py-4">
          {t('platform.blueprint-version.versionchangesmodal.no_changes_recorded')}
        </div>
      );
    }

    return (
      <div className="bg-gray-900 border border-gray-700 rounded-md p-4 overflow-x-auto">
        <pre className="text-xs font-mono text-green-400 whitespace-pre-wrap break-words">
          {JSON.stringify(version.delta, null, 2)}
        </pre>
      </div>
    );
  };

  // Helper function to render delta changes in a readable format
  const renderVisualChanges = () => {
    if (!version.delta || typeof version.delta !== 'object') {
      return (
        <div className="text-sm text-gray-500 italic text-center py-4">
          {t('platform.blueprint-version.versionchangesmodal.no_changes_recorded')}
        </div>
      );
    }

    // If it's a full snapshot (not in delta format), show appropriate message
    if (parsedChanges.length === 0) {
      return (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>{isCurrentActive ? t('platform.blueprint-version.versionchangesmodal.current_active_version') : t('platform.blueprint-version.versionchangesmodal.full_snapshot')}</strong> This version contains a complete snapshot of the blueprint data.
          </p>
        </div>
      );
    }

    // Render changes grouped by category
    return (
      <div className="space-y-4">
        {Object.entries(groupedChanges).map(([category, changes]) => (
          <div key={category} className="border border-gray-200 rounded-lg">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <h5 className="font-semibold text-sm text-gray-900">{category}</h5>
              <p className="text-xs text-gray-600">{changes.length} change{changes.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="p-3 space-y-2">
              {changes.map((change, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2 bg-white rounded border border-gray-100">
                  <div className="flex-shrink-0 mt-0.5">
                    {change.type === 'added' && <Plus className="w-4 h-4 text-green-600" />}
                    {change.type === 'removed' && <Minus className="w-4 h-4 text-red-600" />}
                    {change.type === 'modified' && <Edit className="w-4 h-4 text-blue-600" />}
                    {change.type === 'reordered' && <MoveVertical className="w-4 h-4 text-purple-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          change.type === 'added' ? 'bg-green-50 text-green-700 border-green-200' :
                          change.type === 'removed' ? 'bg-red-50 text-red-700 border-red-200' :
                          change.type === 'modified' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-purple-50 text-purple-700 border-purple-200'
                        }`}
                      >
                        {change.type.toUpperCase()}
                      </Badge>
                      <span className="text-sm font-medium text-gray-900">{change.description}</span>
                    </div>
                    
                    {change.type === 'modified' && (
                      <div className="text-xs text-gray-600 space-y-1 mt-2">
                        <div className="flex gap-2">
                          <span className="font-medium text-red-600">From:</span>
                          <span className="font-mono bg-red-50 px-2 py-0.5 rounded">{formatValue(change.oldValue)}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="font-medium text-green-600">To:</span>
                          <span className="font-mono bg-green-50 px-2 py-0.5 rounded">{formatValue(change.newValue)}</span>
                        </div>
                      </div>
                    )}
                    
                    {change.type === 'added' && change.newValue && (
                      <div className="text-xs text-gray-600 mt-2">
                        <span className="font-medium text-green-600">Added value:</span>
                        <span className="font-mono bg-green-50 px-2 py-0.5 rounded ml-2">{formatValue(change.newValue)}</span>
                      </div>
                    )}
                    
                    {change.type === 'removed' && change.oldValue && (
                      <div className="text-xs text-gray-600 mt-2">
                        <span className="font-medium text-red-600">Removed value:</span>
                        <span className="font-mono bg-red-50 px-2 py-0.5 rounded ml-2">{formatValue(change.oldValue)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-center justify-center">
            <FileText className="h-5 w-5 text-blue-600" />
            {t('platform.blueprint-version.versionchangesmodal.version_changes')} - v{versionString}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('platform.blueprint-version.versionchangesmodal.detailed_changes_for_this_version')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Version Metadata */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <div>
                <div className="text-xs text-gray-500">{t('platform.blueprint-version.versionchangesmodal.created')}</div>
                <div className="text-sm font-medium">{getTimeAgo(version.created_at)}</div>
                <div className="text-xs text-gray-500">{new Date(version.created_at).toLocaleString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              <div>
                <div className="text-xs text-gray-500">{t('platform.blueprint-version.versionchangesmodal.created_by')}</div>
                <div className="text-sm font-medium">{userName}</div>
              </div>
            </div>
          </div>

          {/* Change Summary */}
          {version.change_summary && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                {t('platform.blueprint-version.versionchangesmodal.change_summary')}
              </h4>
              <p className="text-sm text-blue-800">{version.change_summary}</p>
            </div>
          )}

          {/* Version Type Badge */}
          <div className="flex items-center gap-2">
            {isCurrentActive && (
              <Badge className="bg-green-100 text-green-800 border-green-200 border">
                ACTIVE VERSION
              </Badge>
            )}
            {version.change_severity && version.change_severity !== 'none' && (
              <Badge 
                variant={
                  version.change_severity === 'breaking' 
                    ? 'destructive' 
                    : version.change_severity === 'review'
                    ? 'default'
                    : 'outline'
                }
              >
                {version.change_severity}
              </Badge>
            )}
          </div>

          {/* Delta/Changes Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">
                {t('platform.blueprint-version.versionchangesmodal.changes_made')}
              </h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRawData(!showRawData)}
                className="flex items-center gap-2"
              >
                {showRawData ? (
                  <>
                    <Eye className="w-4 h-4" />
                    <span>Visual View</span>
                  </>
                ) : (
                  <>
                    <Code className="w-4 h-4" />
                    <span>Raw JSON</span>
                  </>
                )}
              </Button>
            </div>
            {showRawData ? renderRawData() : renderVisualChanges()}
          </div>

          {/* Severity Details */}
          {version.severity_details && Object.keys(version.severity_details).length > 0 && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                {t('platform.blueprint-version.versionchangesmodal.severity_details')}
              </h4>
              <pre className="text-xs font-mono text-yellow-800 whitespace-pre-wrap break-words">
                {JSON.stringify(version.severity_details, null, 2)}
              </pre>
            </div>
          )}

          {/* Affected Items Count */}
          {version.affected_items_count !== undefined && version.affected_items_count > 0 && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
              <p className="text-sm text-orange-800">
                <strong>{t('platform.blueprint-version.versionchangesmodal.affected_items')}</strong> {version.affected_items_count} {version.affected_items_count === 1 ? 'item' : 'items'} {version.affected_items_count === 1 ? 'is' : 'are'} affected by these changes.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-center">
          <Button onClick={onClose}>{t('platform.common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default VersionChangesModal;

